<template lang="pug">
  div.amacro
    v-toolbar(dense)
      v-col(cols='8' no-gutters)
        div(@click="showEditDialog").clickable.text-body-2.text-truncate {{codeName}}
          explicit-exports(v-if='explicitExports')
        div(@click="showEditDialog").clickable.text-caption.grey--text.text-truncate {{description}}&nbsp;
          display-tags(v-model='tags' @tag='showEditDialog')
      v-spacer
      bionic-metrics-git-commit(v-if='codeReady'
        type='module'
        :guid='amacro.guid'
        :git-name='amacro.gitName'
        :uncommitted='amacro.uncommitted'
        v-model='amacro.code'
        @input='triggerSave'
        @commit='load')
    bionic-metrics-page-loading(v-if='!codeReady')
    bionic-metrics-monaco-editor.full(
      v-if='codeReady'
      ref='editor'
      v-model='amacro.code'
      @input='codeChange'
      type='module'
      :line='line'
      :gitName='amacro.gitName'
      :guid='amacro.guid')
    v-dialog(v-model='showEditNameDialog' max-width='500px')
      v-card
        form(@submit.prevent='editNameDescriptionTags')
          v-card-text
            v-text-field(label='Code Name' v-model='edits.codeName' type='text' required)
            v-textarea(outlined label='Description' v-model='edits.description' type='text' auto-grow rows='3')
            tags(v-model='edits.tags')
            v-checkbox(v-model='edits.explicitExports'
              label="Explicit Exports"
              persistent-hint
              hint="Check if this module uses explicit exports:")
            pre.code.mb-0 exports = {}; // or module.exports = {};
            a(href='https://docs.bionicmetrics.com/guides/exports.html' target='_blank') Explicit Export Documentation
          v-card-actions
            v-spacer
            v-btn(text @click.native='showEditNameDialog=false') Cancel
            v-btn(type='submit' color='primary' text) Save
</template>

<script>
import _ from 'lodash';
import Vue from 'vue';
import {mapMutations} from 'vuex';
import AMacro from '../../api/amacro';
import DisplayTags from '../../components/DisplayTags';
import ExplicitExports from '../../components/ExplicitExports';
import BionicMetricsGitCommit from '../../components/GitCommit';
import BionicMetricsMonacoEditor from '../../components/MonacoEditor';
import BionicMetricsNoData from '../../components/NoData';
import BionicMetricsPageLoading from '../../components/PageLoading';
import Tags from '../../components/Tags';
import SaveMixin from '../../mixins/save.mixin';
import Notifications from '../../services/notification.service';

export default {
  name: 'BionicMetricsAMacro',
  components: {
    ExplicitExports,
    BionicMetricsGitCommit,
    DisplayTags,
    Tags,
    BionicMetricsMonacoEditor,
    BionicMetricsNoData,
    BionicMetricsPageLoading
  },
  props: {
    guid: {
      type: String
    }
  },
  async mounted() {
    await this.load();
  },
  async beforeDestroy() {
    await this.saveOutstanding();
  },
  mixins: [SaveMixin],
  computed: {
    codeReady() {
      return this.amacro && !_.isUndefined(this.amacro.code);
    },
    saveObject() {
      return this.amacro;
    },
    codeName() {
      return _.get(this.amacro, 'codeName');
    },
    explicitExports() {
      return _.get(this.amacro, 'explicitExports');
    },
    description() {
      return _.get(this.amacro, 'description');
    },
    tags() {
      return _.get(this.amacro, 'tags');
    },
    line() {
      const {line} = this.$route.query;
      if (line) return parseInt(line, 10);
      return null;
    }
  },
  methods: {
    abort() {
      return this.$router.push('/');
    },
    async load() {
      try {
        this.amacro = await AMacro.get(this.guid);
        const title = this.amacro.gitName || this.amacro.codeName;
        document.title = title;
        this.setTitle(title);
        if (!this.amacro.tags) this.amacro.tags = [];
      } catch (error) {
        console.error(error);
        return this.abort();
      }
    },
    async save(amacro) {
      try {
        const {uncommitted} = await AMacro.update(amacro.guid, amacro);
        Vue.set(this.amacro, 'uncommitted', uncommitted);
      } catch (error) {
        console.error(`Error saving amacro definition: ${error.message}`);
        Notifications.errorToast('There was a problem saving the module code', {duration: 30000, retry: this.save});
      }
    },
    async codeChange() {
      await this.triggerSave();
    },
    async editNameDescriptionTags() {
      this.amacro.codeName = this.edits.codeName.replace(/[|!&;$%@"<>()+,\s]/g, '');
      this.amacro.description = this.edits.description;
      this.amacro.tags = this.edits.tags || [];
      this.amacro.explicitExports = this.edits.explicitExports;
      this.showEditNameDialog = false;
      await this.save(this.amacro);
      Notifications.toast('Module Information Updated');
    },
    async revert(content) {
      console.log(`Reverting to ${content}`);
      this.$refs.editor.set(content);
      this.showGitDialog = false;
    },
    showEditDialog() {
      this.edits = {
        codeName: this.amacro.codeName,
        description: this.amacro.description,
        tags: this.amacro.tags,
        explicitExports: this.amacro.explicitExports
      };
      this.showEditNameDialog = !this.showEditNameDialog;
    },
    clearDirty() {
      console.log('Save mixin saved and we are no longer dirty');
    },
    updateBreadcrumb() {
      this.setBreadcrumb([{text: 'Modules', to: '/modules'}, {text: this.amacro.codeName}]);
    },
    ...mapMutations({
      setTitle: 'setTitle',
      setBreadcrumb: 'setBreadcrumb'
    })
  },
  watch: {
    'amacro.codeName': {
      handler: function () { // eslint-disable-line
        this.updateBreadcrumb();
      },
      deep: true
    }
  },
  data() {
    return {
      edits: {},
      amacro: null,
      loading: false,
      showEditNameDialog: false,
      showGitDialog: false
    };
  }
};
</script>

<style lang="scss">
.full {
  width: 100%;
  height: 100%;
}

.amacro {
  height: 100%;
  padding-bottom: 48px; // toolbar dense
}
</style>
